import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import { FaLock, FaFileUpload, FaCheckCircle, FaExclamationTriangle, FaTimes } from 'react-icons/fa';

/**
 * CertificadoNfseModal
 *
 * Upload do certificado digital (.pfx) + senha, usado na emissão de
 * DPS/NFS-e Nacional. Fica separado do formulário principal de
 * EmpresaRegistration DE PROPÓSITO: são dados sensíveis, então o campo de
 * senha e o arquivo nunca ficam no mesmo estado (`formData`) que o resto
 * do cadastro — evita que fiquem "esquecidos" na tela por mais tempo que
 * o necessário, e evita que apareçam sem querer em nenhum outro lugar do
 * componente (ex: num console.log de debug do formData geral).
 *
 * O upload vai direto para o backend, que criptografa (AES-256-GCM) antes
 * de gravar — POST /empresas/nfse-config/certificado. O status exibido
 * abaixo vem de GET /empresas/certificado-nfse/status. Nenhuma das duas rotas
 * leva id de empresa — o backend sempre usa a empresa do usuário logado
 * (nunca aceita um id vindo do cliente, por segurança).
 */
function CertificadoNfseModal({ isOpen, onClose, empresa }) {
  const [arquivo, setArquivo] = useState(null);
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // null = ainda não sabemos
  const { API_BASE_URL } = config;
  const { addToast } = useToast();

  useEffect(() => {
    if (isOpen && empresa) {
      verificarStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, empresa]);

  const verificarStatus = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const empresaId = empresa?.idEmpresa ?? empresa?.id;

      const url = empresaId
        ? `${API_BASE_URL}/empresas/certificado-nfse/status/${empresaId}`
        : `${API_BASE_URL}/empresas/certificado-nfse/status`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStatus(response.data);
    } catch (error) {
      console.error('Erro ao verificar status do certificado:', error);
      setStatus(null);
    }
  };

  const formatarData = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('pt-BR');
  };

  // Decide a "cor" do banner de status a partir do que a API devolveu.
  // 'pendente' = nunca cadastrou nada; 'erro' = cadastrou mas nao pode
  // emitir agora (vencido ou emissao desabilitada); 'alerta' = ok mas
  // vencendo em breve; 'ok' = tudo certo.
  const varianteStatus = (s) => {
    if (!s || !s.cadastrado) return 'pendente';
    if (s.expirado || !s.nfseHabilitada) return 'erro';
    if (s.diasParaVencer !== null && s.diasParaVencer <= 30) return 'alerta';
    return 'ok';
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file && !file.name.toLowerCase().endsWith('.pfx')) {
      addToast('error', 'Selecione um arquivo .pfx válido.');
      return;
    }
    setArquivo(file);
  };

  const limparCamposSensiveis = () => {
    setArquivo(null);
    setSenha('');
  };

  const handleClose = () => {
    limparCamposSensiveis();
    onClose();
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!arquivo) {
      addToast('error', 'Selecione o arquivo .pfx do certificado.');
      return;
    }
    if (!senha) {
      addToast('error', 'Informe a senha do certificado.');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('arquivo', arquivo);
    formDataUpload.append('senha', senha);

    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');

      await axios.post(
        `${API_BASE_URL}/empresas/nfse-config/certificado/${empresa.idEmpresa || empresa.id}`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      addToast('success', 'Certificado enviado e armazenado com segurança!');
      limparCamposSensiveis();
      await verificarStatus(); // recarrega validade/alias/apto, não só um flag manual
    } catch (error) {
      console.error('Erro ao enviar certificado:', error);
      const mensagem =
        (error.response && error.response.data && error.response.data.message) ||
        'Erro ao enviar certificado. Verifique o arquivo e a senha e tente novamente.';
      addToast('error', mensagem);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !empresa) return null;

  const variante = varianteStatus(status);
  const statusStyleMap = {
    pendente: styles.statusPendente,
    ok: styles.statusOk,
    alerta: styles.statusAlerta,
    erro: styles.statusErro
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            <FaLock style={styles.headerIcon} />
            <h3 style={styles.title}>Certificado Digital (NFS-e Nacional)</h3>
          </div>
          <button onClick={handleClose} style={styles.closeButton} type="button">
            <FaTimes />
          </button>
        </div>

        <p style={styles.subtitle}>
          {empresa.nomeFantasia} — o certificado é criptografado antes de ser
          armazenado. A senha nunca fica salva em texto puro em lugar nenhum.
        </p>

        {status !== null && (
          <div style={statusStyleMap[variante]}>
            {status.cadastrado ? (
              <>
                <div style={styles.statusLinha}>
                  {variante === 'ok' || variante === 'alerta' ? <FaCheckCircle /> : <FaExclamationTriangle />}
                  <strong>
                    Certificado cadastrado{status.alias ? ` (${status.alias})` : ''}
                  </strong>
                </div>

                <div style={styles.statusLinha}>
                  Validade: {formatarData(status.validade)}
                  {status.expirado && ' — VENCIDO'}
                  {!status.expirado && status.diasParaVencer !== null && status.diasParaVencer <= 30 &&
                    ` — vence em ${status.diasParaVencer} dia(s)`}
                </div>

                <div style={styles.statusLinha}>
                  {status.nfseHabilitada
                    ? 'Emissão de NFS-e habilitada para esta empresa.'
                    : 'Emissão de NFS-e ainda NÃO habilitada para esta empresa.'}
                </div>

                <div style={{ ...styles.statusLinha, fontWeight: '600' }}>
                  {status.aptoParaEmissao
                    ? '✔ Apto para emitir NFS-e.'
                    : '✘ Ainda não apto para emitir NFS-e — verifique validade/habilitação acima.'}
                </div>

                <div style={styles.statusLinha}>
                  Enviar um novo arquivo abaixo substitui o certificado atual.
                </div>
              </>
            ) : (
              'Nenhum certificado cadastrado ainda para esta empresa.'
            )}
          </div>
        )}

        <form onSubmit={handleUpload} style={styles.form}>
          <div style={styles.fieldContainer}>
            <label style={styles.label}>Arquivo do certificado (.pfx) *</label>
            <div style={styles.fileInputWrapper}>
              <FaFileUpload style={styles.fileIcon} />
              <input
                type="file"
                accept=".pfx"
                onChange={handleFileChange}
                disabled={loading}
                style={styles.fileInput}
              />
            </div>
            {arquivo && <small style={styles.hintText}>Selecionado: {arquivo.name}</small>}
          </div>

          <div style={styles.fieldContainer}>
            <label style={styles.label}>Senha do certificado *</label>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              disabled={loading}
              placeholder="Senha do arquivo .pfx"
              style={styles.inputField}
              autoComplete="new-password"
            />
            <small style={styles.hintText}>
              Trafega só por HTTPS e é criptografada no servidor antes de ser salva —
              nunca fica em texto puro no banco.
            </small>
          </div>

          <div style={styles.buttonContainer}>
            <button type="submit" style={styles.uploadButton} disabled={loading}>
              {loading ? 'Enviando...' : 'Enviar Certificado'}
            </button>
            <button type="button" onClick={handleClose} style={styles.cancelButton} disabled={loading}>
              Fechar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '25px',
    width: '90%',
    maxWidth: '480px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  headerIcon: {
    color: '#6f42c1',
    fontSize: '20px'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    color: '#666',
    cursor: 'pointer'
  },
  subtitle: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '15px'
  },
  statusLinha: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px'
  },
  statusOk: {
    padding: '10px 12px',
    backgroundColor: '#e7f7ec',
    color: '#1e7e34',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '15px'
  },
  statusAlerta: {
    padding: '10px 12px',
    backgroundColor: '#fff3cd',
    color: '#856404',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '15px'
  },
  statusErro: {
    padding: '10px 12px',
    backgroundColor: '#fdecea',
    color: '#a51c11',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '15px'
  },
  statusPendente: {
    padding: '10px 12px',
    backgroundColor: '#fff3cd',
    color: '#856404',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '15px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px'
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    fontWeight: '600',
    fontSize: '14px',
    color: '#333',
    marginBottom: '6px'
  },
  fileInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    border: '2px solid #e9ecef',
    borderRadius: '6px',
    padding: '10px 12px'
  },
  fileIcon: {
    color: '#6f42c1'
  },
  fileInput: {
    border: 'none',
    fontSize: '14px',
    flex: 1
  },
  inputField: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '2px solid #e9ecef',
    borderRadius: '6px',
    boxSizing: 'border-box'
  },
  hintText: {
    color: '#6c757d',
    fontSize: '11px',
    marginTop: '4px'
  },
  buttonContainer: {
    display: 'flex',
    gap: '10px',
    marginTop: '5px'
  },
  uploadButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#6f42c1',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  cancelButton: {
    padding: '12px 20px',
    backgroundColor: '#f1f1f1',
    color: '#333',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  }
};

export default CertificadoNfseModal;